import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Send, Trash2, Heart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import MentionInput from '@/components/MentionInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommentsSheet = ({ post, open, onOpenChange, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');

  useEffect(() => {
    if (open && post) {
      fetchComments();
    }
  }, [open, post]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('BŁĄD ŁADOWANIA');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/posts/${post.id}/comments`, {
        text: newComment.trim()
      });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      onCommentAdded?.(post.id);
    } catch (error) {
      toast.error('BŁĄD');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/admin/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('USUNIĘTO');
    } catch (error) {
      toast.error('BŁĄD');
    }
  };

  const handleLike = async (commentId, isLiked) => {
    try {
      if (isLiked) {
        await axios.delete(`${API}/comments/${commentId}/like`);
      } else {
        await axios.post(`${API}/comments/${commentId}/like`);
      }
      setComments(prev => prev.map(c => 
        c.id === commentId 
          ? { ...c, is_liked: !isLiked, likes_count: (c.likes_count || 0) + (isLiked ? -1 : 1) }
          : c
      ));
    } catch (error) {
      // Silently fail
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'TERAZ';
    if (minutes < 60) return `${minutes}M`;
    if (hours < 24) return `${hours}H`;
    return `${days}D`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[70vh] bg-zom-black border-t-3 border-zom-yellow">
        <SheetHeader className="pb-4 border-b-2 border-zom-yellow">
          <SheetTitle className="text-zom-yellow font-heading text-center tracking-wider">
            {comments.length} KOMENTARZY
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-3 max-h-[calc(70vh-160px)]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-3 border-zom-yellow border-t-transparent animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-zom-white/50 py-8 font-heading">BRAK KOMENTARZY</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="border-2 border-zom-white/30 p-3" data-testid={`comment-${comment.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-zom-yellow text-zom-black flex items-center justify-center flex-shrink-0 border-2 border-zom-black font-heading text-sm">
                    {comment.user_email?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-zom-yellow text-xs font-heading">
                        @{comment.user_email?.split('@')[0]?.toUpperCase()}
                      </span>
                      <span className="text-zom-white/50 text-[10px] font-heading">
                        {formatTime(comment.created_at)}
                      </span>
                    </div>
                    <p className="text-zom-white text-sm font-body break-words">
                      {comment.text}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => handleLike(comment.id, comment.is_liked)}
                        className={`flex items-center gap-1 text-xs font-heading transition-colors ${
                          comment.is_liked 
                            ? 'text-zom-red' 
                            : 'text-zom-white/50 hover:text-zom-red'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${comment.is_liked ? 'fill-current' : ''}`} strokeWidth={2.5} />
                        {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
                      </button>
                      
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(comment.id)}
                          className="text-zom-white/30 hover:text-zom-red transition-colors ml-auto"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment Input */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-zom-black border-t-2 border-zom-yellow">
          <div className="flex gap-2">
            <MentionInput
              value={newComment}
              onChange={setNewComment}
              placeholder="Napisz komentarz..."
              className="flex-1 bg-zom-black border-2 border-zom-white text-zom-white px-3 py-2 font-body text-sm"
              data-testid="comment-input"
              singleLine
            />
            <button
              onClick={handleSubmit}
              disabled={submitting || !newComment.trim()}
              className="px-4 py-2 bg-zom-yellow text-zom-black border-2 border-zom-black disabled:opacity-50 hover:bg-zom-red hover:text-zom-white transition-colors"
              data-testid="submit-comment-btn"
            >
              <Send className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CommentsSheet;
